'use client';

import { cn } from '@lib/util/cn';
import { LoadingImage } from '@modules/common/components/loading-image';
import type React from 'react';
import { useEffect, useRef, useState, useTransition } from 'react';
import type { ProductImageVM } from 'types/view-models';

type ZoomableImageProps = {
  images: ProductImageVM[];
  preload?: boolean;
  productTitle: string;
  selectedImage: number;
};

const ZoomableImage = ({
  images,
  preload = false,
  selectedImage,
  productTitle,
}: ZoomableImageProps): React.JSX.Element => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [offset, setOffset] = useState({ x: 50, y: 50 });

  const offsetResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLButtonElement | null>(null);
  const cachedRectRef = useRef<DOMRect | null>(null);

  const [, startTransition] = useTransition();

  const currentImage = images?.[selectedImage]?.url || '';

  const scheduleOffsetReset = (): void => {
    if (offsetResetRef.current !== null) {
      clearTimeout(offsetResetRef.current);
    }
    offsetResetRef.current = setTimeout(() => setOffset({ x: 50, y: 50 }), 300);
  };

  useEffect(() => {
    return () => {
      if (offsetResetRef.current !== null) {
        clearTimeout(offsetResetRef.current);
      }
    };
  }, []);

  const handleMouseEnter = (): void => {
    // Cache rect on enter — avoids getBoundingClientRect() on every mousemove.
    // Do NOT activate zoom here: the browser fires mouseenter whenever the
    // hit-tested element changes (e.g. after a nav-button click repositions
    // pointer events), which would trigger accidental zooms on image change.
    cachedRectRef.current =
      containerRef.current?.getBoundingClientRect() ?? null;
  };

  const handleMouseLeave = (): void => {
    setIsZoomed(false);
    cachedRectRef.current = null;
    scheduleOffsetReset();
  };

  const handleClick = (): void => {
    setIsZoomed((prev) => !prev);
    if (isZoomed) {
      scheduleOffsetReset();
    }
  };

  const handleMouseMove = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    if (!cachedRectRef.current) {
      cachedRectRef.current =
        containerRef.current?.getBoundingClientRect() ?? null;
    }
    const rect = cachedRectRef.current;
    if (!rect) {
      return;
    }
    if (!isZoomed) {
      setIsZoomed(true);
    }
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    // Defer offset update so it doesn't block pointer event handling
    startTransition(() => setOffset({ x, y }));
  };

  return (
    <button
      ref={containerRef}
      aria-pressed={isZoomed}
      aria-label={`${productTitle} — photo ${selectedImage + 1} of ${images.length}`}
      className="relative h-[375px] cursor-pointer w-[375px] overflow-hidden rounded-lg bg-[#F3F4F3] desktop:w-[520px] desktop:h-[520px] medium:w-[620px] medium:h-[620px] large:h-[660px] large:w-[660px]"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      type="button"
    >
      <div
        className={cn(
          'relative h-full w-full bg-[#F3F4F3] transition-transform duration-300',
          isZoomed ? 'scale-150' : 'scale-100',
        )}
        style={{
          transformOrigin: `${offset.x}% ${offset.y}%`,
        }}
      >
        <LoadingImage
          alt={`${productTitle} — photo ${selectedImage + 1}`}
          className="h-full w-full rounded-lg object-contain mix-blend-multiply"
          fill
          hideSkeleton={!preload}
          fetchPriority={preload ? 'high' : undefined}
          loading={preload ? 'eager' : undefined}
          quality={75}
          sizes="(min-width: 1440px) 660px, (min-width: 1280px) 620px, (min-width: 1100px) 520px, 375px"
          src={currentImage}
        />
      </div>
    </button>
  );
};

export default ZoomableImage;
